import { css } from "@emotion/css";
import { Callout, SectionHeader, Tooltip, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { KubernetesObjectStatus } from "@octopusdeploy/octopus-server-client";
import { TaskState } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import { ObjectStatusIcon } from "~/areas/tasks/components/Task/Kubernetes/ObjectStatusIcon";
import { EmptyState } from "~/areas/tasks/components/Task/Kubernetes/Snapshot/EmptyState";
import type { KubernetesSnapshotStepBaseProps } from "~/areas/tasks/components/Task/Kubernetes/Snapshot/KubernetesSnapshotStepExpander";
import { sortObjectStatusByObjectType } from "~/areas/tasks/components/Task/Kubernetes/sortObjectStatusByObjectType";
const styles = {
    linkStyle: css({
        cursor: "pointer",
    }),
    expandoBodyContainer: css({
        padding: space[16],
        "@media (min-width @screen-sm)": {
            overflowX: "auto",
        },
    }),
    kindContainer: css({
        marginTop: space[16],
        marginBottom: space[16],
        borderLeftWidth: space[1],
        borderLeftStyle: "solid",
        borderLeftColor: themeTokens.color.border.primary,
        borderRightWidth: space[1],
        borderRightStyle: "solid",
        borderRightColor: themeTokens.color.border.primary,
        "@media (min-width: @screen-sm)": {
            minWidth: "800px",
        },
    }),
    sectionHeadingTitle: css({
        fontSize: fontSize["medium"],
        fontWeight: fontWeight["700"],
    }),
};
type KubernetesStepSnapshotStatusProps = KubernetesSnapshotStepBaseProps & {
    stepObjectStatuses: KubernetesObjectStatus[] | undefined;
};
export const KubernetesStepSnapshotStatus = (props: KubernetesStepSnapshotStatusProps) => {
    const data = useMemo(() => {
        if (props.stepObjectStatuses) {
            const dict = Object.entries(props.stepObjectStatuses.reduce<Record<string, KubernetesObjectStatus[]>>((prev, current) => {
                if (prev[current.Kind] === undefined) {
                    prev[current.Kind] = [];
                }
                prev[current.Kind].push(current);
                return prev;
            }, {}));
            sortObjectStatusByObjectType(dict);
            return dict;
        }
        return [];
    }, [props.stepObjectStatuses]);
    const renderData = (data: object) => {
        if (Array.isArray(data)) {
            return data.join(", ");
        }
        return data.toString();
    };
    const isUnsuccessfulTask = props.taskState === TaskState.Failed || props.taskState === TaskState.Canceled || props.taskState === TaskState.TimedOut;
    const showResourceStatus = !(props.isAtomicHelmStep && isUnsuccessfulTask);
    return (<div className={styles.expandoBodyContainer}>
            <EmptyState status={props.stepActivity.Status} isResourceStatusEnabled={props.isResourceStatusEnabled} hasData={(props.stepObjectStatuses?.length || 0) > 0}/>
            {!showResourceStatus && (<Callout type={"warning"} title={"Step verification unavailable"}>
                    The Helm <code>--atomic</code> flag on this step rolled back to a previous revision. We can’t determine the status accurately because objects from different revisions might share the same names.
                </Callout>)}
            {data.map(([kind, objects]) => {
            const anyHasNamespace = objects.some((kos) => !!kos.Namespace);
            return (<div className={styles.kindContainer} key={kind}>
                        <SectionHeader title={<div className={styles.sectionHeadingTitle}>{kind}</div>}/>
                        <LegacyDataTable title={kind}>
                            <LegacyDataTableHeader>
                                <LegacyDataTableRow>
                                    {showResourceStatus && (<LegacyDataTableHeaderColumn aria-label="Status Column" width="10px">
                                            &nbsp;
                                        </LegacyDataTableHeaderColumn>)}
                                    <LegacyDataTableHeaderColumn width={"20%"}>Name</LegacyDataTableHeaderColumn>
                                    {Object.keys(objects[0].Data).map((k) => (<LegacyDataTableHeaderColumn key={k} width={"10%"}>
                                            {k}
                                        </LegacyDataTableHeaderColumn>))}
                                    <LegacyDataTableHeaderColumn>{/* Empty column used for auto alignment */}</LegacyDataTableHeaderColumn>
                                    <LegacyDataTableHeaderColumn width={anyHasNamespace ? "15%" : "30%"}>Target Name</LegacyDataTableHeaderColumn>
                                    {anyHasNamespace && <LegacyDataTableHeaderColumn width={"15%"}>Namespace</LegacyDataTableHeaderColumn>}
                                </LegacyDataTableRow>
                            </LegacyDataTableHeader>
                            <LegacyDataTableBody>
                                {objects.map((obj) => (<LegacyDataTableRow key={`${obj.Name}-${obj.ClusterName}=${obj.Namespace}`}>
                                        {showResourceStatus && (<LegacyDataTableRowColumn>
                                                <ObjectStatusIcon objectStatus={obj.Status} stepStatus={props.stepActivity.Status}/>
                                            </LegacyDataTableRowColumn>)}
                                        <LegacyDataTableRowColumn>
                                            {obj.ManifestId ? (<Tooltip content="View object details">
                                                    <a className={styles.linkStyle} onClick={() => props.showObjectDrawer(obj)}>
                                                        {obj.Name}
                                                    </a>
                                                </Tooltip>) : (<span>{obj.Name}</span>)}
                                        </LegacyDataTableRowColumn>
                                        {Object.values(obj.Data).map((v, i) => (<LegacyDataTableRowColumn key={i}>{renderData(v)}</LegacyDataTableRowColumn>))}
                                        <LegacyDataTableRowColumn>{/* Empty column used for auto alignment */}</LegacyDataTableRowColumn>
                                        <LegacyDataTableRowColumn>{obj.ClusterName}</LegacyDataTableRowColumn>
                                        {obj.Namespace ? <LegacyDataTableRowColumn>{obj.Namespace}</LegacyDataTableRowColumn> : undefined}
                                    </LegacyDataTableRow>))}
                            </LegacyDataTableBody>
                        </LegacyDataTable>
                    </div>);
        })}
        </div>);
};
